import React, { useState, useEffect } from 'react';
import {
  X, RefreshCw, Clock, CheckCircle, XCircle, AlertCircle,
  Play, Loader, GitBranch, ArrowRight, History, BarChart3
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTenantProtection } from '../../../hooks/useTenantProtection';

const WorkflowViewer = ({ workflow, onClose }) => {
  const { getProtectedTenantCode } = useTenantProtection();

  const [workflowDetails, setWorkflowDetails] = useState(null);
  const [executionHistory, setExecutionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    loadWorkflowData();
  }, [workflow]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        loadWorkflowData();
      }, 10000); // Reduced from 3 seconds to 10 seconds to reduce server load
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const loadWorkflowData = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();

      // Load workflow details and execution history in parallel
      const [detailsResponse, historyResponse] = await Promise.all([
        axios.get(`/api/automation/workflows/${workflow.WorkflowID}`, {
          headers: { 'X-Tenant-Code': tenantCode },
        }),
        axios.get(`/api/automation/workflows/${workflow.WorkflowID}/executions`, {
          headers: { 'X-Tenant-Code': tenantCode },
          params: { limit: 20 },
        }),
      ]);

      setWorkflowDetails(detailsResponse.data);
      setExecutionHistory(historyResponse.data);

      // Auto-select the latest execution if none selected
      if (!selectedExecution && historyResponse.data.length > 0) {
        setSelectedExecution(historyResponse.data[0]);
      }
    } catch (error) {
      console.error('Failed to load workflow data:', error);
      toast.error('Failed to load workflow data');
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteWorkflow = async () => {
    try {
      const tenantCode = getProtectedTenantCode();
      await axios.post(
        `/api/automation/workflows/${workflow.WorkflowID}/execute`,
        {},
        { headers: { 'X-Tenant-Code': tenantCode } }
      );
      toast.success('Workflow execution started');
      setAutoRefresh(true);
      setTimeout(() => loadWorkflowData(), 1000);
    } catch (error) {
      console.error('Failed to execute workflow:', error);
      toast.error(error.response?.data?.error || 'Failed to execute workflow');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
      case 'in_progress':
        return <Loader className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'completed':
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'pending':
      case 'waiting':
        return <Clock className="h-5 w-5 text-gray-400" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
      case 'in_progress':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
      case 'completed':
      case 'success':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      case 'failed':
      case 'error':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
      case 'pending':
      case 'waiting':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime) return 'N/A';
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const duration = end - start;
    
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  if (loading && !workflowDetails) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
            <GitBranch className="h-6 w-6 mr-2 text-teal-500" />
            {workflowDetails?.WorkflowName || 'Workflow Viewer'}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {workflowDetails?.Description || 'View workflow execution details and history'}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium transition-colors ${
              autoRefresh
                ? 'border-teal-500 dark:border-teal-400 text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/20'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700'
            }`}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
          </button>
          <button
            onClick={loadWorkflowData}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={handleExecuteWorkflow}
            disabled={!workflowDetails?.IsEnabled}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 disabled:opacity-50"
          >
            <Play className="h-4 w-4 mr-2" />
            Execute Now
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            <X className="h-4 w-4 mr-2" />
            Close
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Executions</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {workflowDetails?.ExecutionCount || 0}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Success Rate</div>
          <div className="mt-1 text-2xl font-semibold text-green-600 dark:text-green-400">
            {workflowDetails?.SuccessRate !== null && workflowDetails?.SuccessRate !== undefined
              ? `${Math.round(workflowDetails.SuccessRate)}%`
              : 'N/A'}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Steps</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {workflowDetails?.StepCount || 0}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</div>
          <div className="mt-1 flex items-center">
            {workflowDetails?.IsEnabled ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                Enabled
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                Disabled
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Execution History */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                <History className="h-5 w-5 mr-2" />
                Execution History
              </h3>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {executionHistory.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No executions yet
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {executionHistory.map((execution) => (
                    <button
                      key={execution.ExecutionID}
                      onClick={() => setSelectedExecution(execution)}
                      className={`w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        selectedExecution?.ExecutionID === execution.ExecutionID
                          ? 'bg-teal-50 dark:bg-teal-900/20'
                          : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(execution.Status)}
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusColor(execution.Status)}`}>
                            {execution.Status}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          #{execution.ExecutionID}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(execution.StartedAt).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Duration: {formatDuration(execution.StartedAt, execution.CompletedAt)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Execution Details */}
        <div className="lg:col-span-2">
          {selectedExecution ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Execution #{selectedExecution.ExecutionID}
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedExecution.Status)}`}>
                    {selectedExecution.Status}
                  </span>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {/* Execution Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Started</div>
                    <div className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                      {new Date(selectedExecution.StartedAt).toLocaleString()}
                    </div>
                  </div>
                  {selectedExecution.CompletedAt && (
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">Completed</div>
                      <div className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                        {new Date(selectedExecution.CompletedAt).toLocaleString()}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Duration</div>
                    <div className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                      {formatDuration(selectedExecution.StartedAt, selectedExecution.CompletedAt)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Steps Completed</div>
                    <div className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                      {selectedExecution.StepsCompleted || 0} / {workflowDetails?.StepCount || 0}
                    </div>
                  </div>
                </div>

                {/* Error Message */}
                {selectedExecution.ErrorMessage && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-red-800 dark:text-red-300">Error</div>
                        <div className="text-sm text-red-700 dark:text-red-400 mt-1">
                          {selectedExecution.ErrorMessage}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step Executions */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Step Execution Timeline
                  </h4>
                  {selectedExecution.StepExecutions && selectedExecution.StepExecutions.length > 0 ? (
                    <div className="space-y-3">
                      {selectedExecution.StepExecutions.map((stepExec, index) => (
                        <div
                          key={stepExec.StepExecutionID}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                              <div className="flex-shrink-0 mt-0.5">
                                {getStatusIcon(stepExec.Status)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <span className="font-medium text-gray-900 dark:text-gray-100">
                                    Step {stepExec.StepOrder + 1}: {stepExec.StepName}
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(stepExec.Status)}`}>
                                    {stepExec.Status}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                  Job: {stepExec.JobName}
                                </div>
                                {stepExec.StartedAt && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Started: {new Date(stepExec.StartedAt).toLocaleString()}
                                  </div>
                                )}
                                {stepExec.CompletedAt && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Completed: {new Date(stepExec.CompletedAt).toLocaleString()}
                                    {' '}({formatDuration(stepExec.StartedAt, stepExec.CompletedAt)})
                                  </div>
                                )}
                                {stepExec.ErrorMessage && (
                                  <div className="text-xs text-red-600 dark:text-red-400 mt-1 p-2 bg-red-50 dark:bg-red-900/20 rounded">
                                    Error: {stepExec.ErrorMessage}
                                  </div>
                                )}
                                {stepExec.RetryCount > 0 && (
                                  <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                                    Retries: {stepExec.RetryCount}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No step execution data available
                    </div>
                  )}
                </div>

                {/* Execution Log */}
                {selectedExecution.ExecutionLog && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Execution Log
                    </h4>
                    <div className="bg-gray-900 text-gray-100 p-3 rounded-md font-mono text-xs overflow-x-auto max-h-64 overflow-y-auto">
                      <pre className="whitespace-pre-wrap">{selectedExecution.ExecutionLog}</pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <BarChart3 className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Select an execution from the history to view details
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowViewer;

